import type { ReactNode } from "react";

/** The ARIA a control needs to link its label, hint and error (WCAG 2.1 AA, ADR-0023). */
export interface FieldAria {
  id: string;
  "aria-invalid"?: true;
  "aria-describedby"?: string;
}

/**
 * A labelled form field: `<label htmlFor>` bound to the control's `id`, an optional hint, and an
 * inline error wired via `aria-describedby` + `aria-invalid` so screen readers announce it on the
 * control (form-validation-stack / ADR-0027). The control is a render prop that receives the ARIA to
 * spread — RHF's `register(...)` merges cleanly alongside it.
 */
export function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  children: (aria: FieldAria) => ReactNode;
}) {
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  const aria: FieldAria = {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy,
  };
  return (
    <div className="rsv-field">
      <label className="rsv-label" htmlFor={id}>
        {label}
      </label>
      {hint ? (
        <span className="rsv-hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {children(aria)}
      {error ? (
        <span className="rsv-error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
